import React, { useEffect, useState } from 'react';
import { adminApi } from '../api';
import { useAuth } from '../AuthContext';
import { TextInput, I18nInput } from '../components/Field';
import { ImagePicker } from '../components/ImagePicker';

const LANGS = ['it', 'en', 'de', 'fr'];
const COLOR_KEYS = [['bg', 'Sfondo'], ['bg2', 'Sfondo 2'], ['accent', 'Accento'], ['brown', 'Marrone'], ['ink', 'Testo']];

const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-paelbi-line bg-white p-6 space-y-4">
    <h3 className="font-display text-lg text-paelbi-ink">{title}</h3>
    {children}
  </div>
);

function ColorSection({ theme, setTheme }) {
  return (
    <Card title="Colori">
      {COLOR_KEYS.map(([k, l]) => (
        <div key={k} className="flex items-center gap-3">
          <input type="color" value={theme?.[k] || '#000000'} onChange={(e) => setTheme(k, e.target.value)} className="h-9 w-12 rounded border border-paelbi-line" />
          <span className="text-sm text-paelbi-ink/70">{l}</span>
          <input value={theme?.[k] || ''} onChange={(e) => setTheme(k, e.target.value)} className="ml-auto w-28 rounded-lg border border-paelbi-line px-2 py-1 text-sm" />
        </div>
      ))}
    </Card>
  );
}

function LanguageSection({ languages, defaultLanguage, onToggle, onDefault }) {
  const active = languages || [];
  return (
    <Card title="Lingue">
      <div className="flex flex-wrap gap-2">
        {LANGS.map((l) => (
          <button type="button" key={l} data-testid={`lang-toggle-${l}`} onClick={() => onToggle(l)}
            className={`rounded-btn px-3 py-1.5 text-sm font-semibold uppercase ${active.includes(l) ? 'bg-paelbi-brown text-white' : 'bg-paelbi-bg2 text-paelbi-ink/70'}`}>{l}</button>
        ))}
      </div>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Lingua predefinita</span>
        <select value={defaultLanguage} onChange={(e) => onDefault(e.target.value)}
          className="w-full rounded-lg border border-paelbi-line bg-white px-3 py-2 text-sm">
          {(active.length ? active : LANGS).map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
      </label>
    </Card>
  );
}

export function Branding() {
  const { tenantId } = useAuth();
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    adminApi.getTenant(tenantId).then(setT);
  }, [tenantId]);

  if (!t) return <p className="text-sm text-paelbi-ink/50">Seleziona una struttura...</p>;

  const setBrand = (k, v) => setT({ ...t, brand: { ...t.brand, [k]: v } });
  const setTheme = (k, v) => setT({ ...t, theme: { ...t.theme, [k]: v } });
  const setContact = (k, v) => setT({ ...t, contacts: { ...t.contacts, [k]: v } });
  const toggleLang = (l) => {
    const has = (t.languages || []).includes(l);
    setT({ ...t, languages: has ? t.languages.filter((x) => x !== l) : [...(t.languages || []), l] });
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await adminApi.updateTenant(t.id, {
        brand: t.brand, theme: t.theme, contacts: t.contacts,
        logo_url: t.logo_url, languages: t.languages, default_language: t.default_language,
      });
      setMsg('Salvato');
    } catch (e) { setMsg('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl text-paelbi-ink">Branding & Config</h1>
      <p className="mt-1 text-paelbi-ink/60">Identità della struttura, colori, logo e lingue.</p>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <Card title="Brand">
          <TextInput label="Nome" value={t.brand?.name} onChange={(v) => setBrand('name', v)} />
          <I18nInput label="Payoff" value={t.brand?.payoff} onChange={(v) => setBrand('payoff', v)} />
          <ImagePicker label="Logo" value={t.logo_url} tenantId={tenantId} onChange={(v) => setT({ ...t, logo_url: v })} />
        </Card>

        <ColorSection theme={t.theme} setTheme={setTheme} />

        <Card title="Contatti">
          <TextInput label="Telefono" value={t.contacts?.phone} onChange={(v) => setContact('phone', v)} />
          <TextInput label="Email" value={t.contacts?.email} onChange={(v) => setContact('email', v)} />
        </Card>

        <LanguageSection
          languages={t.languages}
          defaultLanguage={t.default_language}
          onToggle={toggleLang}
          onDefault={(v) => setT({ ...t, default_language: v })}
        />
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button type="button" data-testid="branding-save-button" onClick={save} disabled={saving} className="rounded-btn bg-paelbi-brown px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Salvo...' : 'Salva'}</button>
        {msg && <span className={`text-sm ${msg.startsWith('Errore') ? 'text-red-600' : 'text-green-700'}`}>{msg}</span>}
      </div>
    </div>
  );
}

export default Branding;
